import usePrivateAxios from "@/common/api/usePrivateAxios"
import { defaultTransformerResponse } from "@/common/constants/defaultTransformerResponse.constant"
import { Mapper } from "@/common/services/mapper"
import { MetaResponse } from "@/common/types/metaResponse"
import { keepPreviousData, useQuery } from "@tanstack/react-query"

type UseBillingEventsProps<T> = {
  filters?: {
    search?: string
    page?: number
    clientCompanyId?: string
    clientBranchId?: string
    equipmentId?: string
    authorId?: string
    productId?: string
    createdAtStartDate?: string
    createdAtEndDate?: string
    establishmentCompanyId?: string;
    establishmentBranchId?: string;
    fromInternalBase?: string;
    fiscalDocumentRequired?: boolean;
    fiscalDocumentStatus?: string;
    isRefunded?: string;
    branchType?: string;
  }
  mapper?: (data: any) => T,
}

export const useBillingEvents = <T,>({
  filters: {
    search = '',
    page,
    clientCompanyId,
    clientBranchId,
    equipmentId,
    authorId,
    productId,
    createdAtStartDate,
    createdAtEndDate,
    establishmentCompanyId,
    fromInternalBase,
    establishmentBranchId,
    fiscalDocumentRequired,
    fiscalDocumentStatus,
    isRefunded,
    branchType,
  } = {},
  mapper = Mapper.default<T>(),
}: UseBillingEventsProps<T>) => {
  const privateAxios = usePrivateAxios()

  const {
    data: { data, meta } = defaultTransformerResponse<T>(),
    isPending,
    ...rest
  } = useQuery({
    queryKey: [
      'billingEvents',
      search,
      page,
      clientCompanyId,
      clientBranchId,
      equipmentId,
      authorId,
      productId,
      createdAtStartDate,
      createdAtEndDate,
      establishmentCompanyId,
      establishmentBranchId,
      fromInternalBase,
      fiscalDocumentRequired,
      fiscalDocumentStatus,
      isRefunded,
      branchType,
    ],
    queryFn: () => {
      return privateAxios.get('/billing/events', {
        params: {
          search,
          page,
          'q[client_branch_id_eq]': clientBranchId,
          'q[client_company_id_eq]': clientCompanyId,
          'q[equipment_id_eq]': equipmentId,
          'q[author_id_eq]': authorId,
          'q[product_id_eq]': productId,
          'q[created_at_gteq]': createdAtStartDate,
          'q[created_at_lteq]': createdAtEndDate,
          'q[establishment_company_id_eq]': establishmentCompanyId,
          'q[target_establishment_branch_id_eq]': establishmentBranchId,
          'q[from_internal_base_eq]': fromInternalBase,
          'q[fiscal_document_required_true]': fiscalDocumentRequired,
          'q[fiscal_document_status_eq]': fiscalDocumentStatus,
          'q[is_refunded]': isRefunded,
          'q[branch_type_eq]': branchType,
        }
      })
    },
    select: (data: { data: { data: any[], meta: MetaResponse } }) => {
      return {
        data: mapper(data?.data?.data),
        meta: data?.data?.meta,
      };
    },
    placeholderData: keepPreviousData,
  })
  return { data, meta, isPending, ...rest }
}
